// ═══════════════════════════════════════════════════════════════════════════════
// Platform Audit Log — Unified view of all admin actions
//
// Two tabs:
//   1. Platform Actions — from the platform DB audit_log table
//   2. App Actions     — from the B2C App DB via fetchB2CAuditLog()
//
// Reuses the existing AuditLogTab pattern from feature-gating.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import { fetchAuditLog, fetchB2CAuditLog } from '@/lib/api';
import { usePlatformStore } from '@/lib/store';
import { Loader2, ChevronDown, Filter, ScrollText } from 'lucide-react';
import { cn } from '@/utils/helpers';
import type { AuditLog, B2CAuditEntry } from '@/types';

// ── Action color map ─────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  create: 'badge-emerald',
  created: 'badge-emerald',
  insert: 'badge-emerald',
  update: 'badge-blue',
  updated: 'badge-blue',
  delete: 'badge-red',
  deleted: 'badge-red',
  plan_features_updated: 'badge-blue',
  user_override_created: 'badge-emerald',
  user_override_deleted: 'badge-red',
  pricing_updated: 'badge-amber',
};

function getActionColor(action: string): string {
  if (ACTION_COLORS[action]) return ACTION_COLORS[action];
  const lower = action.toLowerCase();
  if (lower.includes('create') || lower.includes('insert')) return ACTION_COLORS.create;
  if (lower.includes('delete') || lower.includes('remove')) return ACTION_COLORS.delete;
  if (lower.includes('update') || lower.includes('change')) return ACTION_COLORS.update;
  return 'bg-surface-100 text-surface-500';
}

const PAGE_SIZE = 25;

type Tab = 'platform' | 'app';

// ═══════════════════════════════════════════════════════════════════════════════

export function AuditLogPage() {
  const { tenants, loadTenants } = usePlatformStore();
  const [activeTab, setActiveTab] = useState<Tab>('platform');
  const [tenantFilter, setTenantFilter] = useState('');

  useEffect(() => { loadTenants(); }, [loadTenants]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Platform Audit Log</h1>
        <p className="text-surface-500 mt-1">Track all admin actions across the platform</p>
      </div>

      {/* Tabs + Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex gap-1 p-1 bg-surface-200 rounded-xl">
          {([
            { id: 'platform' as Tab, label: 'Platform Actions' },
            { id: 'app' as Tab, label: 'App Actions' },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-1.5 rounded-lg text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-surface-0 text-surface-900 shadow-sm'
                  : 'text-surface-500 hover:text-surface-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-surface-500" />
          <select
            value={tenantFilter}
            onChange={e => setTenantFilter(e.target.value)}
            className="input-field py-1.5 text-xs w-48"
          >
            <option value="">All Tenants</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'platform' && (
        <PlatformActionsTab tenants={tenants} tenantFilter={tenantFilter} />
      )}
      {activeTab === 'app' && (
        <AppActionsTab tenants={tenants} tenantFilter={tenantFilter} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLATFORM ACTIONS TAB — from platform DB audit_log table
// ═══════════════════════════════════════════════════════════════════════════════

function PlatformActionsTab({ tenants, tenantFilter }: { tenants: { id: string; name: string }[]; tenantFilter: string }) {
  const [entries, setEntries] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAuditLog(tenantFilter || undefined, 200);
      setEntries(data);
    } catch (err) {
      console.error('Failed to load platform audit log:', err);
    }
    setLoading(false);
  }, [tenantFilter]);

  useEffect(() => { load(); }, [load]);

  const tenantMap = new Map(tenants.map(t => [t.id, t.name]));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-16">
        <ScrollText size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No platform audit log entries found.</p>
        <p className="text-xs text-surface-400 mt-1">Actions like tenant creation, feature changes, and plan updates will appear here.</p>
      </div>
    );
  }

  return (
    <div className="glass-panel overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-surface-200">
            <th className="table-header-cell w-40">Timestamp</th>
            <th className="table-header-cell w-32">Action</th>
            <th className="table-header-cell w-32">Resource</th>
            <th className="table-header-cell w-36">Tenant</th>
            <th className="table-header-cell w-10"></th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => {
            const expanded = expandedId === entry.id;
            const ts = new Date(entry.created_at);
            const tenantName = entry.tenant_id ? tenantMap.get(entry.tenant_id) || entry.tenant_id.slice(0, 8) : '— Platform —';
            return (
              <PlatformRow
                key={entry.id}
                entry={entry}
                expanded={expanded}
                tenantName={tenantName}
                ts={ts}
                onToggle={() => setExpandedId(expanded ? null : entry.id)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PlatformRow({ entry, expanded, tenantName, ts, onToggle }: {
  entry: AuditLog; expanded: boolean; tenantName: string; ts: Date; onToggle: () => void;
}) {
  const actionColor = getActionColor(entry.action);
  return (
    <>
      <tr className="table-row cursor-pointer" onClick={onToggle}>
        <td className="table-cell">
          <div className="text-xs text-surface-700">{ts.toLocaleDateString()}</div>
          <div className="text-[10px] text-surface-500">{ts.toLocaleTimeString()}</div>
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', actionColor)}>
            {entry.action.replace(/_/g, ' ')}
          </span>
        </td>
        <td className="table-cell">
          <span className="text-xs text-surface-600">{entry.resource_type.replace(/_/g, ' ')}</span>
        </td>
        <td className="table-cell">
          <span className="text-xs text-surface-600">{tenantName}</span>
        </td>
        <td className="table-cell">
          <ChevronDown className={cn('w-3 h-3 text-surface-400 transition-transform', expanded && 'rotate-180')} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="px-5 py-3 bg-surface-50 border-b border-surface-200">
            <div className="space-y-2">
              <div className="flex gap-4 text-[10px]">
                <span className="text-surface-500">Actor: <span className="text-surface-700 font-mono">{entry.actor_id.slice(0, 8)}...</span></span>
                {entry.resource_id && (
                  <span className="text-surface-500">Resource ID: <span className="text-surface-700 font-mono">{entry.resource_id.slice(0, 8)}...</span></span>
                )}
              </div>
              {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                <pre className="text-[10px] text-surface-600 bg-surface-100 rounded-lg p-3 overflow-x-auto font-mono leading-relaxed">
                  {JSON.stringify(entry.metadata, null, 2)}
                </pre>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APP ACTIONS TAB — from B2C App DB via edge function
// ═══════════════════════════════════════════════════════════════════════════════

function AppActionsTab({ tenants, tenantFilter }: { tenants: { id: string; name: string }[]; tenantFilter: string }) {
  const [entries, setEntries] = useState<B2CAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchB2CAuditLog(tenantFilter || undefined, PAGE_SIZE, offset);
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load app audit log:', err);
    }
    setLoading(false);
  }, [tenantFilter, offset]);

  useEffect(() => { load(); }, [load]);

  // Reset offset when filter changes
  useEffect(() => { setOffset(0); }, [tenantFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const tenantMap = new Map(tenants.map(t => [t.id, t.name]));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-16">
        <ScrollText size={32} className="mx-auto text-surface-400 mb-3" />
        <p className="text-sm text-surface-500">No app audit log entries found.</p>
        <p className="text-xs text-surface-400 mt-1">Feature gating changes, pricing updates, and user overrides will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="glass-panel overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-surface-200">
              <th className="table-header-cell w-40">Timestamp</th>
              <th className="table-header-cell w-36">Event</th>
              <th className="table-header-cell w-32">Actor</th>
              <th className="table-header-cell w-28">Type</th>
              <th className="table-header-cell w-36">Tenant</th>
              <th className="table-header-cell w-10"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => {
              const expanded = expandedId === entry.id;
              return (
                <AppRow
                  key={entry.id}
                  entry={entry}
                  expanded={expanded}
                  tenantName={entry.tenant_id ? tenantMap.get(entry.tenant_id) || entry.tenant_id.slice(0, 8) : '—'}
                  onToggle={() => setExpandedId(expanded ? null : entry.id)}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-surface-500">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="px-3 py-1 text-xs border border-surface-200 rounded-lg disabled:opacity-50 hover:bg-surface-50 transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-xs text-surface-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="px-3 py-1 text-xs border border-surface-200 rounded-lg disabled:opacity-50 hover:bg-surface-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AppRow({ entry, expanded, tenantName, onToggle }: {
  entry: B2CAuditEntry; expanded: boolean; tenantName: string; onToggle: () => void;
}) {
  const eventColor = getActionColor(entry.event_type);
  const ts = new Date(entry.created_at);

  return (
    <>
      <tr className="table-row cursor-pointer" onClick={onToggle}>
        <td className="table-cell">
          <div className="text-xs text-surface-700">{ts.toLocaleDateString()}</div>
          <div className="text-[10px] text-surface-500">{ts.toLocaleTimeString()}</div>
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', eventColor)}>
            {entry.event_type.replace(/_/g, ' ')}
          </span>
        </td>
        <td className="table-cell">
          <div className="text-xs text-surface-700">{entry.actor_email || entry.actor_id.slice(0, 8)}</div>
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium',
            entry.actor_type === 'super_admin' ? 'badge-purple' : 'bg-surface-100 text-surface-500')}>
            {entry.actor_type === 'super_admin' ? 'Super Admin' : 'Tenant Admin'}
          </span>
        </td>
        <td className="table-cell">
          <span className="text-xs text-surface-600">{tenantName}</span>
        </td>
        <td className="table-cell">
          <ChevronDown className={cn('w-3 h-3 text-surface-400 transition-transform', expanded && 'rotate-180')} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-5 py-3 bg-surface-50 border-b border-surface-200">
            <div className="space-y-2">
              <div className="flex gap-4 text-[10px]">
                <span className="text-surface-500">Resource: <span className="text-surface-700">{entry.resource_type}</span></span>
                {entry.resource_id && (
                  <span className="text-surface-500">ID: <span className="text-surface-700 font-mono">{entry.resource_id}</span></span>
                )}
              </div>
              {entry.payload && Object.keys(entry.payload).length > 0 && (
                <pre className="text-[10px] text-surface-600 bg-surface-100 rounded-lg p-3 overflow-x-auto font-mono leading-relaxed">
                  {JSON.stringify(entry.payload, null, 2)}
                </pre>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
